/**
 * TimeLimitCondition - behavior tree condition checking elapsed time limit.
 */

import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Blackboard } from '../../blackboard/Blackboard.js';

export interface TimeLimitConditionOptions extends ConditionEvaluatorOptions {
  timeLimit?: number;
  startTimeKey?: string;
  autoSetStartTime?: boolean;
}

@EngineClass('TimeLimitCondition')
export class TimeLimitCondition extends ConditionEvaluator {
  private timeLimit: number;
  private startTimeKey?: string;
  private autoSetStartTime: boolean;
  private hasSetStartTime: boolean = false;

  constructor(options: TimeLimitConditionOptions = {}) {
    super({
      name: 'TimeLimit',
      ...options
    });

    this.timeLimit = options.timeLimit ?? 10.0;
    this.startTimeKey = options.startTimeKey ?? 'TimeLimitStartTime';
    this.autoSetStartTime = options.autoSetStartTime ?? true;
  }

  protected override onReset(): void {
    super.onReset();
    this.hasSetStartTime = false;
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    const currentTime = Date.now() / 1000; // Convert to seconds

    // Get or set start time
    let startTime = blackboard.getValue<number>(this.startTimeKey!);
    if (startTime === undefined) {
      if (this.autoSetStartTime) {
        startTime = currentTime;
        blackboard.setValue(this.startTimeKey!, startTime);
        this.hasSetStartTime = true;
      } else {
        // No start time available and auto-set is disabled
        return BehaviorStatus.Failure;
      }
    }

    // Calculate elapsed time
    const elapsedTime = currentTime - startTime;

    // Check if we're still within the time limit
    return elapsedTime <= this.timeLimit ? BehaviorStatus.Success : BehaviorStatus.Failure;
  }
}